import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { 
  Stethoscope, 
  Users, 
  UserCheck, 
  Gift,
  CheckCircle,
  Camera,
  MessageSquare,
  Sparkles,
  Search,
  FlaskConical,
  Shield,
  Clock,
  Target,
  Zap
} from 'lucide-react';

const LandHezarPage = () => {
  const [countdown, setCountdown] = useState({
    days: 18,
    hours: 21, 
    minutes: 15,
    seconds: 33
  });

  const [remainingSpots, setRemainingSpots] = useState(3247);
  const [takenSpots, setTakenSpots] = useState(6753);
  const totalSpots = 10000;

  const { ref: counterRef, inView: counterInView } = useInView({
    triggerOnce: true,
    threshold: 0.3
  });

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Simulate decreasing spots
  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSpots(prev => {
        if (prev > 0) {
          const decrease = Math.random() > 0.7 ? 1 : 0;
          setTakenSpots(t => t + decrease);
          return prev - decrease;
        }
        return prev;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const progressPercentage = (takenSpots / totalSpots) * 100;

  return (
    <div className="min-h-screen bg-white font-vazirmatn" dir="rtl">
      
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 py-12 bg-gradient-to-b from-white to-gray-50">
        <div className="w-full max-w-6xl mx-auto">
          
          {/* Header */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="text-6xl lg:text-8xl font-bold text-gray-900 mb-4 tracking-tight"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              درموایز
            </motion.h1>
            <motion.p
              className="text-xl lg:text-2xl text-gray-600 mb-12 font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              دستیار هوشمند پوست تو
            </motion.p>

            {/* Main CTA */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Link to="/login">
                <motion.button 
                  className="bg-gray-900 hover:bg-gray-800 text-white px-12 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  شروع تحلیل رایگان
                </motion.button>
              </Link>
              <p className="text-sm text-red-500 mt-4 font-medium">
                ظرفیت محدود: فقط ۱۰ هزار نفر اول
              </p>
            </motion.div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            
            {/* Left - Image */}
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-gray-100">
                <img 
                  src="/land1.png" 
                  alt="درموایز - تحلیل پوست" 
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                />
                
                {/* Medical Badge */}
                <motion.div
                  className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-gray-200"
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="flex items-center gap-2 text-gray-700">
                    <Stethoscope size={18} />
                    <span className="font-medium text-sm">تأیید پزشکی</span>
                  </div>
                </motion.div>
              </div>

              {/* Features under image */}
              <div className="mt-8 space-y-4">
                {[
                  { text: "تحلیل دقیق پوست با هوش مصنوعی", color: "bg-blue-50 border-blue-100 text-blue-800" },
                  { text: "برنامه مراقبتی شخصی‌سازی شده", color: "bg-green-50 border-green-100 text-green-800" },
                  { text: "بررسی سازگاری محصولات با پوست", color: "bg-gray-50 border-gray-200 text-gray-700" }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    className={`${feature.color} rounded-lg p-4 border`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle size={18} />
                      <span className="font-medium text-sm">{feature.text}</span>
                    </div>
                  </motion.div>
                ))}
                
                {/* CTA under features */}
                <motion.div 
                  className="pt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  <Link to="/login">
                    <motion.button 
                      className="w-full bg-gray-900 hover:bg-gray-800 text-white py-4 rounded-lg font-semibold shadow-lg transition-all duration-300"
                      whileHover={{ scale: 1.02, y: -1 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      شروع رایگان
                    </motion.button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Counter & Info */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {/* User Counter */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="bg-gray-50 rounded-lg p-4 mb-8 text-center border border-gray-200">
                  <div className="font-semibold text-gray-700 flex items-center justify-center gap-2">
                    <Gift size={20} />
                    برای ۱۰ هزار کاربر اول کاملاً رایگان
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-8 mb-8" ref={counterRef}>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900 mb-2">
                      {counterInView && (
                        <CountUp
                          end={remainingSpots}
                          duration={2}
                          separator=","
                          useEasing={true}
                        />
                      )}
                    </div>
                    <div className="text-gray-600 font-medium flex items-center justify-center gap-2 text-sm">
                      <Users size={18} />
                      ظرفیت باقی‌مانده
                    </div>
                  </div>

                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {counterInView && (
                        <CountUp
                          end={takenSpots}
                          duration={2}
                          separator=","
                          useEasing={true}
                        />
                      )}
                    </div>
                    <div className="text-gray-600 font-medium flex items-center justify-center gap-2 text-sm">
                      <UserCheck size={18} />
                      ثبت‌نام شده
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="bg-gray-100 rounded-full h-3 mb-4 overflow-hidden">
                  <motion.div
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 2.5, delay: 1 }}
                  />
                </div>

                <p className="text-center text-gray-600 text-sm">
                  {progressPercentage.toFixed(1)}% از ظرفیت پر شده
                </p>
              </div>

              {/* Price */}
              <div className="bg-gray-900 text-white rounded-2xl p-6 text-center shadow-lg">
                <div className="text-sm text-gray-400 mb-2">قیمت عادی</div>
                <div className="text-xl text-gray-400 line-through mb-3">۴۹۰,۰۰۰ تومان</div>
                <div className="text-3xl font-bold text-green-400 mb-2">
                  رایگان برای ۳ ماه
                </div>
                <div className="text-sm text-gray-400">صرفه‌جویی ۱,۴۷۰,۰۰۰ تومان</div>
              </div>

              {/* Prizes */}
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">🏆 جوایز ماهانه</h3>
                  <p className="text-gray-600 text-sm">قرعه‌کشی بین تمام کاربران</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                    <div className="text-2xl mb-2">👨‍⚕️</div>
                    <div className="text-lg font-bold text-gray-900">۲۰ پکیج</div>
                    <div className="text-gray-600 text-sm">مشاوره متخصص</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                    <div className="text-2xl mb-2">🎁</div>
                    <div className="text-lg font-bold text-gray-900">۵ پکیج</div>
                    <div className="text-gray-600 text-sm">محصولات مراقبتی</div>
                    <div className="text-xs text-gray-500 mt-1">ارزش: ۵ میلیون تومان</div>
                  </div>
                </div>

                {/* Timer */}
                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 text-center">زمان تا قرعه‌کشی</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'روز', value: countdown.days },
                      { label: 'ساعت', value: countdown.hours },
                      { label: 'دقیقه', value: countdown.minutes },
                      { label: 'ثانیه', value: countdown.seconds }
                    ].map((item, index) => (
                      <div key={item.label} className="bg-gray-50 border border-gray-200 text-gray-700 rounded p-2 text-center">
                        <div className="text-lg font-bold">{item.value.toString().padStart(2, '0')}</div>
                        <div className="text-xs">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <Link to="/login">
                <motion.button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold shadow-lg transition-all duration-300"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  همین الان شروع کن
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">چطور کار می‌کنه؟</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              فقط سه مرحله ساده تا دریافت تحلیل کامل پوست
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "۱",
                title: "عکس بگیر",
                description: "یک عکس ساده از صورت در نور مناسب",
                icon: <Camera size={32} className="text-gray-600" />,
                color: "blue"
              },
              {
                step: "۲", 
                title: "سوالات پاسخ بده",
                description: "چند سوال کوتاه درباره پوست و سبک زندگی",
                icon: <MessageSquare size={32} className="text-gray-600" />,
                color: "green"
              },
              {
                step: "۳",
                title: "برنامه دریافت کن", 
                description: "تحلیل کامل و روتین شخصی‌سازی شده",
                icon: <Sparkles size={32} className="text-gray-600" />,
                color: "gray"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className={`bg-white rounded-xl p-8 text-center shadow-lg border border-gray-200 ${
                  item.color === 'blue' ? 'border-t-4 border-t-blue-500' :
                  item.color === 'green' ? 'border-t-4 border-t-green-500' :
                  'border-t-4 border-t-gray-500'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="mb-4 flex justify-center">{item.icon}</div>
                <div className="text-2xl font-bold text-gray-900 mb-2">مرحله {item.step}</div>
                <h3 className="text-lg font-bold text-gray-700 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
                              <Link to="/login">
              <motion.button 
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-4 rounded-lg font-semibold shadow-lg transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                الان شروع کن - رایگان
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Product Analysis */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">تحلیل محصولات</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              قبل از خرید، بفهم محصول به پوستت می‌سازه یا نه
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                {[
                  {
                    title: "اسکن محصول",
                    description: "عکس محصول بگیر یا اسمش رو تایپ کن",
                    icon: <Search size={24} />,
                    color: "blue-50"
                  },
                  {
                    title: "تحلیل ترکیبات",
                    description: "بررسی کامل مواد با پروفایل پوستت",
                    icon: <FlaskConical size={24} />,
                    color: "green-50"
                  },
                  {
                    title: "نتیجه دقیق",
                    description: "مناسب، نامناسب یا احتیاط - با دلیل کامل",
                    icon: <CheckCircle size={24} />,
                    color: "gray-50"
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className={`bg-${item.color} rounded-lg p-6 border border-gray-200`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="text-gray-600 mt-1">{item.icon}</div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  مزایای تحلیل محصولات
                </h3>
                <div className="space-y-4">
                  {[
                    "صرفه‌جویی در خرید محصولات نامناسب",
                    "جلوگیری از آلرژی و عوارض جانبی", 
                    "انتخاب محصولات مناسب پوستت",
                    "تحلیل علمی ترکیبات",
                    "مقایسه با پایگاه داده پزشکی"
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3 text-gray-700"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <CheckCircle size={18} className="text-green-600" />
                      <span>{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            viewport={{ once: true }}
          >
                              <Link to="/login">
              <motion.button 
                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg font-semibold shadow-lg transition-all duration-300"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                محصولات رو هم تست کن - رایگان
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Dermowize */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">چرا درموایز؟</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              تنها پلتفرم هوش مصنوعی تحلیل پوست در ایران
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "هوش مصنوعی پیشرفته",
                description: "آخرین تکنولوژی‌های CV و ML",
                icon: <Zap size={32} className="text-blue-600" />,
                accent: "blue"
              },
              {
                title: "مبنای علمی",
                description: "مطابق یافته‌های دوماتولوژی",
                icon: <FlaskConical size={32} className="text-green-600" />,
                accent: "green"
              },
              {
                title: "شخصی‌سازی کامل",
                description: "هر پوست منحصربه‌فرد",
                icon: <Target size={32} className="text-gray-600" />,
                accent: "gray"
              },
              {
                title: "امنیت بالا",
                description: "حفاظت از حریم خصوصی",
                icon: <Shield size={32} className="text-blue-600" />,
                accent: "blue"
              },
              {
                title: "پشتیبانی ۲۴/۷",
                description: "همیشه در کنارتان",
                icon: <Clock size={32} className="text-green-600" />,
                accent: "green"
              },
              {
                title: "آپدیت مداوم",
                description: "بهبود مستمر سیستم",
                icon: <Sparkles size={32} className="text-gray-600" />,
                accent: "gray"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className={`bg-white rounded-xl p-6 text-center shadow-lg border border-gray-200 ${
                  item.accent === 'blue' ? 'border-t-4 border-t-blue-500' :
                  item.accent === 'green' ? 'border-t-4 border-t-green-500' :
                  'border-t-4 border-t-gray-500'
                }`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">سوالات متداول</h2>
            <p className="text-lg text-gray-600">پاسخ سوالات رایج درباره درموایز</p>
          </motion.div>

          <div className="space-y-6">
            {[
              {
                question: "آیا واقعاً رایگان است؟",
                answer: "بله، سه ماه اول برای ۱۰ هزار نفر نخست کاملاً رایگان است. هیچ پرداخت خودکاری انجام نمی‌شود."
              },
              {
                question: "عکس من چه می‌شود؟",
                answer: "تصاویر فقط برای پردازش تحلیل استفاده می‌شود. می‌توانید در تنظیمات درخواست حذف کنید."
              },
              {
                question: "نتیجه تحلیل چقدر طول می‌کشد؟",
                answer: "معمولاً چند ثانیه. اگر اینترنت ضعیف باشد ممکن است کمی بیشتر طول بکشد."
              },
              {
                question: "آیا این جایگزین پزشک است؟",
                answer: "نه، توصیه‌ها جنبه راهنمای مراقبتی دارند. برای مشکلات جدی با پزشک مشورت کنید."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-lg p-6 border border-gray-200"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.01 }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                <p className="text-gray-600 leading-relaxed">{item.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              آماده‌ای شروع کنی؟
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              هزاران نفر قبل از تو تجربه‌شون رو با درموایز تغییر دادن
            </p>

            {/* Quick Stats */}
            <motion.div 
              ref={counterRef}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 border border-white/20"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">
                    {counterInView && (
                      <CountUp
                        end={remainingSpots}
                        duration={2}
                        separator=","
                        useEasing={true}
                      />
                    )}
                  </div>
                  <div className="text-gray-300 font-medium">ظرفیت باقی‌مانده</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400 mb-2">
                    {countdown.hours}:{countdown.minutes}:{countdown.seconds}
                  </div>
                  <div className="text-gray-300 font-medium">تا قرعه‌کشی بعدی</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <Link to="/login">
                <motion.button 
                  className="bg-white hover:bg-gray-100 text-gray-900 px-12 py-6 rounded-xl font-bold text-xl shadow-2xl transition-all duration-300"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  همین الان شروع کن - کاملاً رایگان
                </motion.button>
              </Link>
              
              <p className="text-gray-400 mt-4 font-medium">
                عجله کن! فقط برای ۱۰ هزار نفر اول
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="max-w-4xl mx-auto text-center px-6">
          <p className="text-gray-400">
            © ۲۰۲۵ درموایز - تمامی حقوق محفوظ است
          </p>
        </div>
      </footer>

    </div>
  );
};

export default LandHezarPage;